import React, { useCallback, useEffect, useState } from 'react';
import {
  Clock, Search, RefreshCw, MoreVertical, XCircle, Download, CheckCircle
} from 'lucide-react';
import './ClicksignAcompanhar.css';

const STATUS_INFO = {
  running: { rotulo: 'Em processo', classe: 'andamento' },
  closed: { rotulo: 'Finalizado', classe: 'finalizado' },
  canceled: { rotulo: 'Cancelado', classe: 'cancelado' },
  draft: { rotulo: 'Rascunho', classe: 'rascunho' }
};

const ABAS_STATUS = [
  ['running', 'Em processo'],
  ['closed', 'Finalizados'],
  ['canceled', 'Cancelados'],
  ['todos', 'Todos']
];

async function api(path, options = {}) {
  const res = await fetch(`/api/clicksign-envios${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { error: text };
  }
  if (!res.ok) throw new Error(data.error || 'Erro na requisição');
  return data;
}

const fmtData = (valor) => {
  if (!valor) return '';
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};

const VAZIO = { data: [], total: 0, totalPages: 1, counts: {} };

export default function ClicksignAcompanhar() {
  const [modo, setModo] = useState('documentos');
  const [status, setStatus] = useState('running');
  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState('');
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState(VAZIO);
  const [carregando, setCarregando] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [menuAberto, setMenuAberto] = useState(null);

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) { setCarregando(true); setErro(''); }
    try {
      const params = new URLSearchParams({ status, page: String(pagina), limit: '20' });
      if (termo) params.set('q', termo);
      setDados(await api(`?${params.toString()}`));
    } catch (err) {
      if (!silencioso) {
        setErro(err.message);
        setDados(VAZIO);
      }
    } finally {
      setCarregando(false);
    }
  }, [status, pagina, termo]);

  useEffect(() => { carregar(); }, [carregar]);

  // Relê a lista sozinha a cada 60 s (só lê o banco local, não gasta a cota da Clicksign).
  useEffect(() => {
    const timer = setInterval(() => {
      if (!document.hidden) carregar(true);
    }, 60000);
    return () => clearInterval(timer);
  }, [carregar]);

  useEffect(() => {
    if (!menuAberto) return undefined;
    const fechar = () => setMenuAberto(null);
    document.addEventListener('click', fechar);
    return () => document.removeEventListener('click', fechar);
  }, [menuAberto]);

  function buscar(event) {
    event.preventDefault();
    setPagina(1);
    setTermo(busca.trim());
  }

  function limparBusca() {
    setBusca('');
    setTermo('');
    setPagina(1);
  }

  function trocarStatus(novo) {
    setStatus(novo);
    setPagina(1);
  }

  async function atualizarStatus() {
    setAtualizando(true);
    setErro('');
    setAviso('');
    try {
      const r = await api('/atualizar', { method: 'POST', body: JSON.stringify({}) });
      let msg = `${r.atualizados} envio(s) atualizado(s)`;
      if (r.restantes > 0) msg += `; ainda restam ${r.restantes}, clique de novo para continuar`;
      if (r.erros && r.erros.length) msg += `; ${r.erros.length} com erro (${r.erros[0].erro})`;
      setAviso(msg);
      await carregar();
    } catch (err) {
      setErro(err.message);
    } finally {
      setAtualizando(false);
    }
  }

  async function importar() {
    setImportando(true);
    setErro('');
    setAviso('');
    try {
      const r = await api('/importar', { method: 'POST', body: JSON.stringify({}) });
      let msg = `${r.importados} envio(s) importado(s), ${r.jaExistiam} já estavam na lista`;
      if (r.erros && r.erros.length) msg += `, ${r.erros.length} com erro`;
      setAviso(msg);
      await carregar();
    } catch (err) {
      setErro(err.message);
    } finally {
      setImportando(false);
    }
  }

  async function cancelar(envio) {
    setMenuAberto(null);
    const nome = envio.envelope_name || envio.document_name || 'este envio';
    if (!window.confirm(`Cancelar "${nome}"? Essa ação não pode ser desfeita.`)) return;
    try {
      setErro('');
      await api(`/${envio.envelope_id}/cancelar`, { method: 'POST', body: JSON.stringify({}) });
      setAviso('Envio cancelado');
      await carregar();
    } catch (err) {
      setErro(err.message);
    }
  }

  async function baixar(envio, tipo) {
    setMenuAberto(null);
    setErro('');
    try {
      const tipos = tipo === 'ambos' ? ['original', 'assinado'] : [tipo];
      for (const t of tipos) {
        const { url } = await api(`/${envio.envelope_id}/arquivo?tipo=${t}`);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
        await new Promise((resolve) => setTimeout(resolve, 700));
      }
    } catch (err) {
      setErro(err.message);
    }
  }

  const contagem = (chave) => (
    chave === 'todos'
      ? Object.values(dados.counts || {}).reduce((soma, n) => soma + Number(n || 0), 0)
      : Number((dados.counts || {})[chave] || 0)
  );

  return (
    <div className="ca-page">
      <div className="ca-topo">
        <div className="ca-icone"><Clock size={22} /></div>
        <div>
          <h1 className="ca-titulo">Acompanhar Envios</h1>
          <p className="ca-sub">Veja o andamento dos documentos enviados para assinatura na ClickSign.</p>
        </div>
        <div className="ca-acoes">
          <button type="button" className="ca-btn" onClick={importar} disabled={importando}>
            <Download size={15} /> {importando ? 'Importando...' : 'Importar da Clicksign'}
          </button>
          <button type="button" className="ca-btn ca-btn-destaque" onClick={atualizarStatus} disabled={atualizando}>
            <RefreshCw size={15} className={atualizando ? 'ca-girando' : ''} />
            {atualizando ? 'Atualizando...' : 'Atualizar status'}
          </button>
        </div>
      </div>

      {erro && <div className="ca-alerta ca-alerta-erro" role="alert">{erro}</div>}
      {aviso && <div className="ca-alerta ca-alerta-ok">{aviso}</div>}

      <form className="ca-busca-form" onSubmit={buscar}>
        <div className="ca-busca">
          <Search size={16} />
          <input
            type="text"
            placeholder="Busque por documento, envelope ou signatário"
            value={busca}
            onChange={(event) => {
              const valor = event.target.value;
              setBusca(valor);
              // Apagou o texto: tira o filtro sozinho, sem precisar apertar Buscar de novo.
              if (!valor.trim() && termo) {
                setTermo('');
                setPagina(1);
              }
            }}
          />
        </div>
        <button type="submit" className="ca-btn">Buscar</button>
        {(busca || termo) && (
          <button type="button" className="ca-btn" onClick={limparBusca}>Limpar</button>
        )}
      </form>

      <div className="ca-abas">
        <button type="button" className={`ca-aba ${modo === 'documentos' ? 'ativa' : ''}`} onClick={() => setModo('documentos')}>
          Ver documentos
        </button>
        <button type="button" className={`ca-aba ${modo === 'envelopes' ? 'ativa' : ''}`} onClick={() => setModo('envelopes')}>
          Ver envelopes
        </button>
      </div>

      <div className="ca-filtros-status">
        {ABAS_STATUS.map(([valor, rotulo]) => (
          <button
            type="button"
            key={valor}
            className={`ca-chip ${status === valor ? 'ativo' : ''}`}
            onClick={() => trocarStatus(valor)}
          >
            {rotulo} ({contagem(valor)})
          </button>
        ))}
      </div>

      <div className="ca-lista-caixa">
        {carregando && <div className="ca-vazio">Carregando...</div>}
        {!carregando && !erro && dados.data.length === 0 && (
          <div className="ca-vazio">
            Nenhum envio encontrado. Os envios feitos a partir de agora aparecem aqui automaticamente;
            para trazer os que já estão na Clicksign, use "Importar da Clicksign".
          </div>
        )}

        {!carregando && dados.data.length > 0 && (
          <ul className="ca-lista">
            {dados.data.map((envio) => {
              const info = STATUS_INFO[envio.status] || { rotulo: envio.status, classe: 'rascunho' };
              const assinantes = Array.isArray(envio.signers) ? envio.signers : [];
              const total = envio.assinaturas_total || assinantes.length || 0;
              const finalizado = envio.status === 'closed';
              const feitas = finalizado ? total : (envio.assinaturas_feitas || 0);
              const titulo = modo === 'documentos'
                ? (envio.document_name || envio.envelope_name)
                : (envio.envelope_name || envio.document_name);
              const subtitulo = modo === 'documentos' ? envio.envelope_name : '1 documento';

              return (
                <li className="ca-linha" key={envio.envelope_id}>
                  <div className="ca-col-doc">
                    <div className="ca-titulo-doc" title={titulo}>{titulo}</div>
                    {subtitulo && <div className="ca-subtitulo" title={subtitulo}>{subtitulo}</div>}
                  </div>

                  <div className="ca-col-assinaturas">
                    <div className="ca-contador">
                      {feitas}/{total} Assinatura{total === 1 ? '' : 's'}
                    </div>
                    {assinantes.map((a) => (
                      <div className="ca-assinante" key={a.signer_key || a.email || a.name}>
                        {finalizado
                          ? <CheckCircle size={14} className="ca-ok" />
                          : <Clock size={14} className="ca-pendente" />}
                        <span>{a.email || a.phone_number || a.name}</span>
                      </div>
                    ))}
                  </div>

                  <div className="ca-col-status">
                    <span className={`ca-badge ca-badge-${info.classe}`}>{info.rotulo}</span>
                    {envio.status === 'running' && envio.deadline_at && (
                      <div className="ca-data">Até {fmtData(envio.deadline_at)}</div>
                    )}
                    <div className="ca-data">Criado em {fmtData(envio.created_date)}</div>
                    {envio.criado_por && <div className="ca-data">por {envio.criado_por}</div>}
                  </div>

                  <div className="ca-col-menu">
                    <button
                      type="button"
                      className="ca-kebab"
                      title="Ações"
                      onClick={(event) => {
                        event.stopPropagation();
                        setMenuAberto(menuAberto === envio.envelope_id ? null : envio.envelope_id);
                      }}
                    >
                      <MoreVertical size={18} />
                    </button>
                    {menuAberto === envio.envelope_id && (
                      <div className="ca-menu" onClick={(event) => event.stopPropagation()}>
                        {envio.status === 'running' && envio.pode_cancelar && (
                          <button type="button" onClick={() => cancelar(envio)}>
                            <XCircle size={15} /> Cancelar
                          </button>
                        )}
                        <button type="button" onClick={() => baixar(envio, 'original')}>
                          <Download size={15} /> Baixar documento original
                        </button>
                        <button type="button" disabled={!finalizado} onClick={() => baixar(envio, 'assinado')}>
                          <Download size={15} /> Baixar documento assinado
                        </button>
                        <button type="button" disabled={!finalizado} onClick={() => baixar(envio, 'ambos')}>
                          <Download size={15} /> Baixar original e assinado
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {dados.totalPages > 1 && (
        <div className="ca-paginacao">
          <button type="button" className="ca-btn" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>Anterior</button>
          <span>Página {pagina} de {dados.totalPages}</span>
          <button type="button" className="ca-btn" disabled={pagina >= dados.totalPages} onClick={() => setPagina(pagina + 1)}>Próxima</button>
        </div>
      )}
    </div>
  );
}
